import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import * as teamsApi from '../../api/teams'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Spinner from '../../components/common/Spinner'
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'

export default function TeamsOverview() {
  const [searchQuery, setSearchQuery] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['teams', { search: searchQuery }],
    queryFn: () => teamsApi.getTeams({ 
      search: searchQuery,
      limit: 1000
    }),
  })

  const teams = data?.data || []

  // Filter teams locally for instant feedback
  const filteredTeams = teams.filter(team => {
    const matchesSearch = !searchQuery ||
      team.team_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.uid?.toLowerCase().includes(searchQuery.toLowerCase())
    
    return matchesSearch
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Teams</h1>
          <p className="mt-2 text-gray-600">
            {filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <Link to="/teams/new">
          <Button variant="primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Team
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card>
        <Card.Body>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search teams by name, age group, or level..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </Card.Body>
      </Card>

      {/* Teams Grid */}
      {filteredTeams.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No teams found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery 
                ? 'Try adjusting your search criteria' 
                : 'Get started by creating a new team'}
            </p>
            {!searchQuery && (
              <Link to="/teams/new" className="inline-block mt-4">
                <Button variant="primary">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Team
                </Button>
              </Link>
            )}
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeams.map((team) => (
            <Card key={team.id} className="hover:shadow-lg transition-shadow">
              <Card.Body>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {team.team_name || 'Unnamed Team'}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">{team.uid}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {team.category && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Category:</span> {team.category}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <Link to={`/teams/${team.id}`}>
                    <Button variant="secondary" size="sm" className="w-full">
                      View Details
                    </Button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}